/**
 * US States with abbreviations and full names
 * Used throughout the app for state selection dropdowns
 */
export const US_STATES = [
  { value: "AL", label: "Alabama" },
  { value: "AK", label: "Alaska" },
  { value: "AZ", label: "Arizona" },
  { value: "AR", label: "Arkansas" },
  { value: "CA", label: "California" },
  { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" },
  { value: "DE", label: "Delaware" },
  { value: "DC", label: "District of Columbia" },
  { value: "FL", label: "Florida" },
  { value: "GA", label: "Georgia" },
  { value: "HI", label: "Hawaii" },
  { value: "ID", label: "Idaho" },
  { value: "IL", label: "Illinois" },
  { value: "IN", label: "Indiana" },
  { value: "IA", label: "Iowa" },
  { value: "KS", label: "Kansas" },
  { value: "KY", label: "Kentucky" },
  { value: "LA", label: "Louisiana" },
  { value: "ME", label: "Maine" },
  { value: "MD", label: "Maryland" },
  { value: "MA", label: "Massachusetts" },
  { value: "MI", label: "Michigan" },
  { value: "MN", label: "Minnesota" },
  { value: "MS", label: "Mississippi" },
  { value: "MO", label: "Missouri" },
  { value: "MT", label: "Montana" },
  { value: "NE", label: "Nebraska" },
  { value: "NV", label: "Nevada" },
  { value: "NH", label: "New Hampshire" },
  { value: "NJ", label: "New Jersey" },
  { value: "NM", label: "New Mexico" },
  { value: "NY", label: "New York" },
  { value: "NC", label: "North Carolina" },
  { value: "ND", label: "North Dakota" },
  { value: "OH", label: "Ohio" },
  { value: "OK", label: "Oklahoma" },
  { value: "OR", label: "Oregon" },
  { value: "PA", label: "Pennsylvania" },
  { value: "RI", label: "Rhode Island" },
  { value: "SC", label: "South Carolina" },
  { value: "SD", label: "South Dakota" },
  { value: "TN", label: "Tennessee" },
  { value: "TX", label: "Texas" },
  { value: "UT", label: "Utah" },
  { value: "VT", label: "Vermont" },
  { value: "VA", label: "Virginia" },
  { value: "WA", label: "Washington" },
  { value: "WV", label: "West Virginia" },
  { value: "WI", label: "Wisconsin" },
  { value: "WY", label: "Wyoming" },
] as const;

/**
 * US States with full state name as both value and label
 * Used for forms that need the full state name stored
 */
export const US_STATES_FULL_NAME = US_STATES.map((state) => ({
  value: state.label,
  label: state.label,
}));

/**
 * 50 Most Common Car Makes in the US Market
 * Used for vehicle valuation dropdown
 */
export const COMMON_CAR_MAKES = [
  "Toyota",
  "Ford",
  "Chevrolet",
  "Honda",
  "Nissan",
  "Jeep",
  "Ram",
  "Hyundai",
  "Subaru",
  "Kia",
  "GMC",
  "Mazda",
  "Volkswagen",
  "Mercedes-Benz",
  "BMW",
  "Dodge",
  "Lexus",
  "Buick",
  "Cadillac",
  "Audi",
  "Tesla",
  "Acura",
  "Volvo",
  "Chrysler",
  "Lincoln",
  "Mitsubishi",
  "Land Rover",
  "Porsche",
  "Genesis",
  "Infiniti",
  "Mini",
  "Alfa Romeo",
  "Jaguar",
  "Maserati",
  "Bentley",
  "Ferrari",
  "Lamborghini",
  "Rolls-Royce",
  "McLaren",
  "Aston Martin",
  "Lotus",
  "Bugatti",
  "Rivian",
  "Lucid",
  "Polestar",
  "Fisker",
  "Vinfast",
  "Scout",
  "Ineos",
  "Other",
] as const;

export const ELAYNE_TASK_IDS = [
  "notify_credit_bureaus",
  "do_not_mail",
  "asset_search",
  "request_probate_guide",
] as const;

export const LEGAL_ROLE_OPTIONS: { value: string; label: string }[] = [
  { value: "Executor", label: "Executor" },
  { value: "Co-executor", label: "Co-executor" },
  { value: "Administrator", label: "Administrator" },
  { value: "Beneficiary", label: "Beneficiary" },
  { value: "Trustee", label: "Trustee" },
  { value: "Grantor", label: "Grantor" },
  { value: "Guardian", label: "Guardian" },
  { value: "Attorney", label: "Attorney" },
  { value: "Financial Advisor", label: "Financial Advisor" },
  { value: "Other", label: "Other" },
];

const TEST_USER_IDS: string[] = [
  "auth0|66f59625c17cfd63fa88415e", // local
  "auth0|66bb95284417428611d85855", // dev
  "auth0|677ec3723bda1f389d6c1e37", // prod
];

export const getCurrentEnvironment = (): "local" | "prod" | "dev" => {
  const env = import.meta.env.NODE_ENV;
  if (env === "development") return "local";
  if (env === "production") return "prod";
  return "dev";
};

export const getCurrentTestUserIds = (): string[] => {
  return TEST_USER_IDS;
};

export const SUPPORT_PHONE = {
  display: "(646) 692-0630",
  href: "tel:+16466920630",
} as const;

export const EMAIL_PATTERN = /^([a-zA-Z0-9._%-+]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6})*$/;
export const PASSWORD_PATTERN = /(?=(.*[0-9]))((?=.*[A-Za-z0-9])(?=.*[A-Z])(?=.*[a-z]))^.{8,}$/;

/**
 * Supabase/PostgreSQL Error Code Constants
 *
 * This file contains semantic constants for Supabase error codes to improve
 * code readability and maintainability. Instead of using raw error codes like
 * 'PGRST116' or '23505', use these semantic constants.
 *
 * @see https://postgrest.org/en/stable/api.html#errors-and-http-status-codes
 * @see https://www.postgresql.org/docs/current/errcodes-appendix.html
 */

// PostgREST specific error codes
export const POSTGREST_ERROR_CODES = {
  /** No rows found - when a query returns no results */
  NO_ROWS_FOUND: "PGRST116",
} as const;

// PostgreSQL error codes
export const POSTGRES_ERROR_CODES = {
  /** Unique violation - when trying to insert/update a record that violates a unique constraint */
  UNIQUE_VIOLATION: "23505",
  /** Foreign key violation - when trying to insert/update a record that violates a foreign key constraint */
  FOREIGN_KEY_VIOLATION: "23503",
  /** Not null violation - when trying to insert/update a record with a null value in a not-null column */
  NOT_NULL_VIOLATION: "23502",
  /** Check violation - when trying to insert/update a record that violates a check constraint */
  CHECK_VIOLATION: "23514",
} as const;

export const SUPABASE_ERROR_CODES = {
  ...POSTGREST_ERROR_CODES,
  ...POSTGRES_ERROR_CODES,
} as const;

export type SupabaseErrorCode = (typeof SUPABASE_ERROR_CODES)[keyof typeof SUPABASE_ERROR_CODES];

export const ONBOARDING_STORAGE_KEYS = {
  DATA: "onboardingData",
  SESSION: "onboardingSession",
  TASK_ID: "onboardingTaskId",
  OBIT: "onboarding_obit",
  CODE: "onboarding_code",
  UTM: "onboarding_utm",
  PERSON_CREATED: "onboarding_person_created",
  PENDING_EMAIL: "onboardingPendingEmail",
  PENDING_PASSWORD: "onboardingPendingPassword",
} as const;

export const REF_PILL_TEXT: Record<string, string> = {
  cta_legal: "Settle the estate without a law degree",
  cta_admin: "The estate's to-do list handled for you",
  cta_financial: "Find all the assets in the estate",
  cta_identity: "Stop identity theft after death",
};

export const DEFAULT_PILL_TEXT = "Estate settlement done for you";

export function getRefPillText(ref: string): string {
  return REF_PILL_TEXT[ref] ?? DEFAULT_PILL_TEXT;
}
